/**
 * Text-token interpolation (`specs/M6-A1-automation-engine.md` §5.7).
 *
 * Every form monday emits must resolve, and an **unresolvable token renders as its literal
 * text** — never empty, never an error, so a typo in a message is visible instead of silent.
 *
 * The current builder board-qualifies everything it writes: typing `{pulse.name} on
 * {board.name}` stores `{<boardId>.pulse.name} on {<boardId>.board.name}`
 * (`discovery/automations/sandbox-experiments.md` E11). The bare forms are legacy and still
 * occur 78 times in the production census, so both are handled here.
 */
/** `{` + anything that is not a brace + `}`. Nested braces are not a monday form. */
const TOKEN = /\{([^{}]*)\}/g;
export function interpolateTokens(text, context) {
    return text.replace(TOKEN, (literal, body) => resolveToken(body, context) ?? literal);
}
/** Returns `undefined` when the token cannot be resolved, so the caller keeps the literal. */
export function resolveToken(body, context) {
    const parts = body.split('.');
    // A board-qualified token: `{<boardId>.pulse.<col>}` / `{<boardId>.board.name}`.
    if (parts.length >= 3 && /^\d+$/.test(parts[0] ?? '')) {
        const boardId = parts[0];
        const scoped = boardId === context.boardId ? context : context.boards?.[boardId];
        if (scoped === undefined)
            return undefined;
        return resolveToken(parts.slice(1).join('.'), { ...scoped, boardId });
    }
    if (parts.length !== 2) {
        // `{.user.name}` (a real, malformed token on this account) has an empty first part and must
        // render literally; so does anything else that is not `<scope>.<field>`.
        return context.extra?.[body];
    }
    const [scope, field] = parts;
    switch (scope) {
        case 'pulse':
            if (field === 'name')
                return context.itemName;
            // Resolution order: column id first, then exact-case title (§5.7, ASSUMED).
            return context.columnText?.[field] ?? context.columnTextByTitle?.[field];
        case 'board':
            return field === 'name' ? context.boardName : undefined;
        case 'user':
            return field === 'name' ? context.userName : undefined;
        default:
            return context.extra?.[body];
    }
}
/** Every token in a string, for the builder's validation and for `references`. */
export function tokensIn(text) {
    return [...text.matchAll(TOKEN)].map((match) => match[1]);
}
//# sourceMappingURL=tokens.js.map